/**
 * Child side of the iframe bridge. Active only when the configurator runs inside an iframe.
 *
 * Messages to the parent (window.parent.postMessage), all shaped as
 *   { source: 'dakkapel-configurator', type: <string>, ...payload }
 *
 *   ready        { path, height }   page (re)loaded inside the iframe
 *   height       { height }         document height changed (ResizeObserver), for "auto" height iframes
 *   quote-sent   { url }            quote request submitted; url = thanks page inside the iframe
 *
 * Messages from the parent (window.postMessage into the iframe), same shape:
 *
 *   layout       { paddingTop }     px the parent overlays on top of the iframe (its menu); becomes --embed-top
 *   ping         {}                 answered with `height`
 *
 * The parent counterpart is public/embed.js.
 */
const SOURCE = 'dakkapel-configurator';

function parentOrigin() {
  try {
    if (window.location.ancestorOrigins && window.location.ancestorOrigins.length) {
      return window.location.ancestorOrigins[0];
    }
  } catch (e) {
    // ignore
  }
  return '*';
}

function documentHeight() {
  const doc = document.documentElement;
  const body = document.body;
  return Math.max(
    body ? body.scrollHeight : 0,
    body ? body.offsetHeight : 0,
    doc.scrollHeight,
    doc.offsetHeight
  );
}

const bridge = {
  active: window.self !== window.top,
  origin: parentOrigin(),
  lastHeight: 0,

  emit(type, payload = {}) {
    if (!this.active) {
      return;
    }
    window.parent.postMessage({ source: SOURCE, type, ...payload }, this.origin);
  },

  sendHeight(force = false) {
    const height = documentHeight();
    if (force || height !== this.lastHeight) {
      this.lastHeight = height;
      this.emit('height', { height });
    }
  },

  setPaddingTop(px) {
    const value = Math.max(0, parseInt(px, 10) || 0);
    document.documentElement.style.setProperty('--embed-top', value + 'px');
    document.body.classList.add('embedded');
    // Sticky offsets changed: let the Vue app rescale its canvas.
    window.dispatchEvent(new Event('resize'));
    this.sendHeight(true);
  },

  handleMessage(event) {
    const data = event.data;
    if (!data || data.source !== SOURCE) {
      return;
    }
    if (this.origin !== '*' && event.origin !== this.origin) {
      return;
    }
    switch (data.type) {
      case 'layout':
        this.setPaddingTop(data.paddingTop);
        break;
      case 'ping':
        this.sendHeight(true);
        break;
      default:
        break;
    }
  },

  init() {
    if (!this.active) {
      return;
    }

    document.body.classList.add('embedded');
    window.addEventListener('message', (event) => this.handleMessage(event));

    let frame = null;
    const scheduleHeight = () => {
      if (frame) {
        return;
      }
      frame = window.requestAnimationFrame(() => {
        frame = null;
        this.sendHeight();
      });
    };

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(scheduleHeight);
      observer.observe(document.documentElement);
      if (document.body) {
        observer.observe(document.body);
      }
    }
    window.addEventListener('resize', scheduleHeight);
    window.addEventListener('load', () => this.sendHeight(true));

    this.emit('ready', { path: window.location.pathname + window.location.search, height: documentHeight() });
  },
};

window.DakkapelEmbed = bridge;

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => bridge.init());
} else {
  bridge.init();
}

export default bridge;
